"use client";

import type { ReactNode } from "react";
import type { Key } from "react-aria-components";

import { TabList as RACTabList, Tab as RACTab } from "react-aria-components";
import { clsx } from "clsx";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

import { MediaRow } from "@ui/components/core/MediaList";

export interface STTUseCaseItem {
  title: string;
  descriptor: string;
  icon?: ReactNode;
}

export interface STTUseCaseTabListProps {
  items: STTUseCaseItem[];
  selectedKey: Key;
}

export function STTUseCaseTabList({ items, selectedKey }: STTUseCaseTabListProps) {
  const selectedIdx = typeof selectedKey === "number" ? selectedKey : -1;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);

  const updateScrollFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const epsilon = 2;
    const maxScroll = scrollWidth - clientWidth;
    setFadeLeft(scrollLeft > epsilon);
    setFadeRight(maxScroll > epsilon && scrollLeft < maxScroll - epsilon);
  }, []);

  useLayoutEffect(() => {
    updateScrollFades();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollFades, { passive: true });
    const ro = new ResizeObserver(updateScrollFades);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollFades);
      ro.disconnect();
    };
  }, [items.length, selectedKey, updateScrollFades]);

  return (
    <div className='tw-relative tw-flow-root lg:tw-contents tw-pt-3 lg:tw-pt-0'>
      {/* Horizontal scroll fade edges — only visible on mobile */}
      <>
        <div
          className={clsx(
            "tw-absolute tw-top-0 tw-bottom-0 tw-w-12 tw-z-10 tw-pointer-events-none -tw-left-full-bleed lg:tw-hidden tw-transition-opacity tw-duration-300 tw-bg-[linear-gradient(to_right,var(--marker-background-color,white)_0%,transparent_100%)]",
            !fadeLeft && "tw-opacity-0",
          )}
        />
        <div
          className={clsx(
            "tw-absolute tw-top-0 tw-bottom-0 tw-w-12 tw-z-10 tw-pointer-events-none -tw-right-full-bleed lg:tw-hidden tw-transition-opacity tw-duration-300 tw-bg-[linear-gradient(to_left,var(--marker-background-color,white)_0%,transparent_100%)]",
            !fadeRight && "tw-opacity-0",
          )}
        />
      </>

      {/* Scroll container */}
      <div
        ref={scrollRef}
        className='tw-flex tw-flex-row tw-gap-3 tw-overflow-x-auto no-scrollbar tw-scrollbar-none -tw-mx-full-bleed tw-px-full-bleed lg:tw-flex-col lg:tw-gap-0.5 lg:tw-overflow-x-visible lg:tw-mx-0 lg:tw-px-0'
      >
        <RACTabList aria-label='Use-case demos' className='tw-contents'>
          {items.map((item, idx) => {
            const isSelected = idx === selectedIdx;

            return (
              <RACTab
                key={idx}
                id={idx}
                className='tw-group tw-shrink-0 tw-outline-none lg:tw-ml-[-12px] focus-visible:tw-outline-focus focus-visible:tw-rounded-2xl tw-cursor-pointer'
              >
                <MediaRow
                  disableTransition
                  isSelected={isSelected}
                  className={clsx(!isSelected && "group-hover:tw-bg-cream")}
                >
                  <div
                    className={clsx(
                      "tw-w-7 tw-h-7 tw-rounded-[10px] tw-flex tw-items-center tw-justify-center tw-shrink-0",
                      isSelected ? "tw-bg-black" : "tw-bg-gray-400 group-hover:tw-bg-black",
                    )}
                  >
                    {item.icon}
                  </div>
                  <span
                    className={clsx(
                      "tw-type-sm tw-text-black",
                      !isSelected && "tw-opacity-40 group-hover:tw-opacity-100",
                    )}
                  >
                    {item.title}
                  </span>
                  <span
                    className={clsx(
                      "tw-type-xs tw-text-black/60 tw-ml-auto tw-hidden lg:tw-block",
                      !isSelected && "lg:tw-opacity-60 lg:group-hover:tw-opacity-100",
                    )}
                  >
                    {item.descriptor}
                  </span>
                </MediaRow>
              </RACTab>
            );
          })}
        </RACTabList>
      </div>
    </div>
  );
}

export default STTUseCaseTabList;
